import { FormEvent, useEffect, useRef } from "react";
import { DropdownV2, FormCardV2, FormGridV2, InputV2 } from "../FormControlsV2";
import { FormActions } from "./FormActions";
import { FarmFormProps, FarmFormValueV2 } from "./types";
import { createAutomaticFarmInitials, isDisabled, parseCoords, stringifyCoords } from "./utils";
import { calculateHectares } from "../../utils/geolib/calculateHectares";
import { useTranslation } from "react-i18next";

export function FarmFormV2({
  value,
  mode = "create",
  errors = {},
  disabledFields = [],
  cardTitle,
  cardDescription,
  showCardHeader = true,
  managers,
  states,
  cities,
  autoGenerateInitials = true,
  mapSlot,
  seasonSlot,
  headerActions,
  showActions = true,
  onChange,
  onBlur,
  onSubmit,
  onCancel,
  onStateChange,
}: FarmFormProps) {
  const { t } = useTranslation();
  const previousAutoInitials = useRef("");
  const latest = useRef({ value, onChange, managers });
  const managerDisabled = isDisabled("manager_user_id", mode, disabledFields);
  const hasManager = !!value.manager_user_id;
  const farmId = value.uuid ?? value.id;
  const singleManagerId = managers.length === 1 ? managers[0].value : undefined;
  const hectares = value.map_coords?.length >= 3 ? calculateHectares(value.map_coords) : "0";

  function update(partial: Partial<FarmFormValueV2>) {
    onChange({ ...value, ...partial });
  }

  function handleNameChange(name: string) {
    if (!autoGenerateInitials) {
      update({ name });
      return;
    }

    const nextInitials = createAutomaticFarmInitials(name);
    const currentMatchesAutoInitials = !value.initials || value.initials === previousAutoInitials.current;
    previousAutoInitials.current = nextInitials;
    update({
      name,
      initials: currentMatchesAutoInitials ? nextInitials : value.initials,
    });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit && onSubmit(value);
  }

  useEffect(() => {
    latest.current = { value, onChange, managers };
  }, [value, onChange, managers]);

  useEffect(() => {
    if (managerDisabled || hasManager || singleManagerId === undefined) return;
    const current = latest.current;
    current.onChange({ ...current.value, manager_user_id: current.managers[0] });
  }, [farmId, managerDisabled, hasManager, singleManagerId]);

  return (
    <form className="ov2-property-form" onBlur={onBlur} onSubmit={handleSubmit}>
      <FormCardV2
        title={showCardHeader ? cardTitle || t("formsV2.farm") : undefined}
        description={showCardHeader ? cardDescription : undefined}
        actions={headerActions}
      >
        <div className="ov2-property-form__layout">
          <FormGridV2>
            <InputV2
              name="name"
              label={t("formsV2.farmName")}
              value={value.name}
              disabled={isDisabled("name", mode, disabledFields)}
              error={errors.name}
              onChange={handleNameChange}
            />
            <InputV2
              name="initials"
              label={t("formsV2.initials")}
              value={value.initials}
              maxLength={3}
              disabled={isDisabled("initials", mode, disabledFields)}
              error={errors.initials}
              onChange={(initials) => update({ initials: initials.toUpperCase() })}
            />
            <InputV2
              name="document"
              label="CPF / CNPJ"
              value={value.document}
              mask="cpfCnpj"
              disabled={isDisabled("document", mode, disabledFields)}
              error={errors.document}
              onChange={(document) => update({ document })}
            />
            <InputV2
              name="state_registration"
              label={t("formsV2.stateRegistration")}
              value={value.state_registration}
              disabled={isDisabled("state_registration", mode, disabledFields)}
              error={errors.state_registration}
              onChange={(state_registration) => update({ state_registration })}
            />
            <DropdownV2
              name="address.state"
              label={t("formsV2.state")}
              value={value.address.state}
              options={states}
              searchable
              disabled={isDisabled("address.state", mode, disabledFields)}
              error={errors["address.state"]}
              onChange={(state) => {
                onStateChange && onStateChange(state);
                onChange({ ...value, address: { ...value.address, state, city: null } });
              }}
            />
            <DropdownV2
              name="address.city"
              label={t("formsV2.city")}
              value={value.address.city}
              options={cities}
              searchable
              disabled={isDisabled("address.city", mode, disabledFields) || !value.address.state}
              error={errors["address.city"]}
              onChange={(city) => onChange({ ...value, address: { ...value.address, city } })}
            />
            <DropdownV2
              name="manager_user_id"
              label={t("formsV2.farmManager")}
              value={value.manager_user_id}
              options={managers}
              searchable
              disabled={isDisabled("manager_user_id", mode, disabledFields)}
              error={errors.manager_user_id}
              onChange={(manager_user_id) => update({ manager_user_id })}
            />
            <div className="ov2-property-form__season">
              <label className="ov2-field__label">{t("formsV2.seasonDefinition")}</label>
              {seasonSlot || (
                <div className="ov2-property-form__inline">
                  <InputV2
                    name="season.rain_initial"
                    label={t("formsV2.rainySeasonStart")}
                    type="number"
                    value={value.season.rain_initial}
                    disabled={isDisabled("season.rain_initial", mode, disabledFields)}
                    error={errors["season.rain_initial"]}
                    onChange={(rain_initial) =>
                      update({ season: { ...value.season, rain_initial: Number(rain_initial) } })
                    }
                  />
                  <InputV2
                    name="season.dry_initial"
                    label={t("formsV2.drySeasonStart")}
                    type="number"
                    value={value.season.dry_initial}
                    disabled={isDisabled("season.dry_initial", mode, disabledFields)}
                    error={errors["season.dry_initial"]}
                    onChange={(dry_initial) =>
                      update({ season: { ...value.season, dry_initial: Number(dry_initial) } })
                    }
                  />
                </div>
              )}
            </div>
          </FormGridV2>

          <div className="ov2-property-form__map">
            {mapSlot || (
              <InputV2
                name="map_coords"
                label={t("formsV2.mapCoordinates")}
                type="textarea"
                value={stringifyCoords(value.map_coords)}
                disabled={isDisabled("map_coords", mode, disabledFields)}
                error={errors.map_coords}
                onChange={(coords) => update({ map_coords: parseCoords(coords) })}
              />
            )}
            <div className="ov2-property-acreage-tag">
              <span>{t("formsV2.totalArea")}</span>
              <strong>{hectares} ha</strong>
            </div>
          </div>
        </div>
      </FormCardV2>

      {showActions && <FormActions mode={mode} onCancel={onCancel} />}
    </form>
  );
}
